import { useMemo } from 'react';
import { isToday, startOfDay } from 'date-fns';

import { Box, Link, Stack, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';
import { TaskLinkedEntityType } from 'src/services/tasks/tasks.types';
import { useGetCallListMembershipsQuery } from 'src/services/call-lists/call-lists.service';
import { CallListPersonType, CallListEntryStatus } from 'src/services/call-lists/call-lists.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

import { formatDate } from '../utils';
import { useCallListsEnabled } from '../hooks/use-call-lists-enabled';

// ----------------------------------------------------------------------

// Only people go on call lists.
const PERSON_TYPES: Partial<Record<TaskLinkedEntityType, CallListPersonType>> = {
  [TaskLinkedEntityType.CONTACT]: CallListPersonType.CONTACT,
  [TaskLinkedEntityType.CANDIDATE]: CallListPersonType.CANDIDATE,
};

export type LinkedCall = {
  entryId: string;
  nextCallAt: string | null;
  attemptNumber: number;
  maxAttempts: number;
  /** The caller's own list name, or null for someone else's list. */
  listName: string | null;
  /** Whose list it is, for someone else's list. */
  ownerName: string | null;
  /** Opens this person's card on the call list; only for the caller's own lists. */
  href: string | null;
};

/**
 * The next call on each active call list that has this person (JT1-1827), soonest first. The call
 * list holds the calls, so a profile's Tasks block shows them from there rather than as Call tasks.
 */
export function useLinkedCalls(entityType: TaskLinkedEntityType, entityId: string): LinkedCall[] {
  const enabled = useCallListsEnabled();
  const personType = PERSON_TYPES[entityType];

  const { currentData } = useGetCallListMembershipsQuery(
    { entityType: personType as CallListPersonType, entityId },
    { skip: !enabled || !personType || !entityId }
  );

  return useMemo(() => {
    if (!currentData) return [];

    const mine: LinkedCall[] = currentData.mine
      .filter((membership) => membership.status === CallListEntryStatus.ACTIVE)
      .map((membership) => ({
        entryId: membership.entry_id,
        nextCallAt: membership.next_call_at,
        attemptNumber: membership.attempt_number,
        maxAttempts: membership.max_attempts,
        listName: membership.call_list_name,
        ownerName: null,
        href: paths.dashboard.tasks.callList({ listId: membership.call_list_id, entryId: membership.entry_id }),
      }));

    const others: LinkedCall[] = currentData.others
      .filter((shared) => !shared.is_mine)
      .map((shared) => ({
        entryId: shared.entry_id,
        nextCallAt: shared.next_call_at,
        attemptNumber: shared.attempt_number,
        maxAttempts: shared.max_attempts,
        listName: null,
        ownerName: shared.owner ? `${shared.owner.first_name} ${shared.owner.last_name}`.trim() : null,
        href: null,
      }));

    // No next call yet means due now, so those come first.
    const at = (call: LinkedCall) => (call.nextCallAt ? new Date(call.nextCallAt).getTime() : 0);
    return [...mine, ...others].sort((a, b) => at(a) - at(b));
  }, [currentData]);
}

// ----------------------------------------------------------------------

/** One next call in a profile's Tasks block, styled like the tasks beside it. */
export function LinkedCallItem({ call }: { call: LinkedCall }) {
  const { t } = useTranslate();

  const overdue = !!call.nextCallAt && new Date(call.nextCallAt) < startOfDay(new Date());

  const when = (() => {
    if (!call.nextCallAt) return t('call_lists.linked.due_now');
    if (isToday(new Date(call.nextCallAt))) return t('call_lists.linked.today');
    return t('call_lists.linked.next_call', { date: formatDate(call.nextCallAt) });
  })();

  const title = call.listName
    ?? (call.ownerName ? t('call_lists.linked.theirs', { name: call.ownerName }) : t('call_lists.linked.theirs_unknown'));

  return (
    <Box
      data-testid={`linked-call-${call.entryId}`}
      sx={{ p: 1.5, borderRadius: 1, border: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Label variant="soft" color="info" startIcon={<Iconify icon="solar:phone-bold" />}>
          {t('call_lists.linked.label')}
        </Label>

        {call.href ? (
          <Link component={RouterLink} href={call.href} variant="subtitle2" color="text.primary" underline="hover">
            {title}
          </Link>
        ) : (
          <Typography variant="subtitle2">{title}</Typography>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" columnGap={2} rowGap={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: overdue ? 'error.main' : 'text.secondary' }}>
          <Iconify icon="mdi:clock-outline" width={14} />
          <Typography variant="caption" color="inherit">{when}</Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {t('call_lists.attempt_of', { current: call.attemptNumber, max: call.maxAttempts })}
        </Typography>
      </Stack>
    </Box>
  );
}
